import React, { createContext, useEffect, useReducer } from 'react'

import { Loader } from 'components'

import { Auth, Hub } from 'aws-amplify'
import { OpenAPI } from 'generated'
import { localAuthHeaders, setSessionUserId } from 'utils/localAuth'
import useToast from 'hooks/useToast'

import {
  AuthActionProps,
  AWSCognitoContextType,
  InitialLoginContextProps,
  RegistrationAttributes,
  UserAttributes,
} from 'types'

// constant
const initialState: InitialLoginContextProps = {
  isLoggedIn: false,
  isInitialized: false,
  isNewPasswordRequired: false,
  email: '',
  user: null,
}

// ==============================|| AWS Cognito CONTEXT & PROVIDER ||============================== //

const AWSCognitoContext = createContext<AWSCognitoContextType | null>(null)

export const AWSCognitoProvider = ({
  children,
}: {
  children: React.ReactElement
}) => {
  const reducer = (state = initialState, action: AuthActionProps) => {
    switch (action.type) {
      case 'login': {
        const { user } = action.payload!
        const jwt = user?.signInUserSession?.accessToken?.jwtToken

        OpenAPI.TOKEN = jwt
        // Capture the identity here as well as in index.tsx: that one only
        // runs on a retry, so without this the first requests after a fresh
        // sign-in would carry no x-user-id.
        // Amplify types this as `AccessToken`, which does not declare the
        // decoded claims, so read them through a structural cast.
        const claims = user?.signInUserSession?.accessToken as
          | { payload?: { sub?: string } }
          | undefined
        setSessionUserId(claims?.payload?.sub)

        // Resolved per request, and merged with the local identity headers:
        // assigning a plain object here used to replace the resolver installed
        // in index.tsx, so after sign-in a local backend saw no x-user-id and
        // answered 401 to everything.
        OpenAPI.HEADERS = async () => ({
          'Content-Type': 'application/json',
          ...localAuthHeaders(),
        })

        // axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`

        return {
          ...state,
          isLoggedIn: true,
          isInitialized: true,
          user,
        }
      }
      case 'new_password_required': {
        return {
          ...state,
          isNewPasswordRequired: !state.isNewPasswordRequired,
        }
      }
      case 'logout': {
        OpenAPI.TOKEN = ''

        return {
          ...state,
          isInitialized: true,
          isLoggedIn: false,
          user: null,
        }
      }
      case 'email': {
        const { email } = action.payload!
        return {
          ...state,
          email,
        }
      }
      case 'email_verified': {
        return {
          ...state,
          user: {
            ...state.user,
            attributes: {
              ...state?.user?.attributes,
              email_verified: true,
            },
          },
        }
      }
      default: {
        return { ...state }
      }
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState)
  const { toast } = useToast()

  useEffect(() => {
    const init = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser()
        if (user) {
          dispatch({
            type: 'login',
            payload: {
              user,
            },
          })
        } else {
          dispatch({ type: 'logout' })
        }
      } catch (err) {
        console.log(err)
        dispatch({ type: 'logout' })
      }
    }

    init()
  }, [])

  const login = async (email: string, password: string) => {
    try {
      const user = await Auth.signIn(email.toLowerCase(), password)

      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        dispatch({ type: 'new_password_required' })
      }

      dispatch({
        type: 'login',
        payload: {
          user,
        },
      })
    } catch (err: any) {
      toast({ message: err?.message || 'Login Error', alertColor: 'error' })
    }
  }

  const refreshToken = async () => {
    try {
      const data = await Auth.currentSession()
      const accessToken = data.getAccessToken()
      const jwt = accessToken.getJwtToken()
      // axios.defaults.headers.common['Authorization'] = `Bearer ${jwt}`
      OpenAPI.TOKEN = jwt
      toast({
        message: 'Your session was successfully updated. Try again',
        alertColor: 'info',
      })
    } catch (err: any) {
      toast({ message: err?.message || 'Login Error', alertColor: 'error' })
    }
  }

  const register = async (
    email: string,
    password: string,
    fullname: string,
    phone_number?: string,
    marketing_consent?: boolean
  ) => {
    // try {
    const username = email.replace('@', 'a.').toLowerCase()

    let attributes: RegistrationAttributes = {
      name: fullname,
      email: email.toLowerCase(),
    }

    if (phone_number) {
      attributes['phone_number'] = phone_number
    }

    if (marketing_consent) {
      attributes['custom:marketing_consent'] = marketing_consent + ''
    }

    await Auth.signUp({
      username,
      password,
      attributes,
      autoSignIn: { enabled: true },
    })
    toast({
      message: 'Please confirm your registration. Check email.',
      alertColor: 'success',
    })

    dispatch({
      type: 'email',
      payload: { email: username },
    })

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'register',
    })
    // } catch (err: any) {

    // } finally {

    // }
  }

  const logout = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      if (user) {
        await Auth.signOut({ global: true })
        dispatch({ type: 'logout' })
      }
    } catch (err: any) {
      toast({ message: err?.message || 'Logout Error', alertColor: 'error' })
    }
  }

  const resetPassword = async (email: string) => {
    try {
      await Auth.forgotPassword(email.toLowerCase())
      toast({
        message: 'Code was sent. Check your email.',
        alertColor: 'success',
      })

      dispatch({
        type: 'email',
        payload: { email: email.toLowerCase() },
      })
    } catch (err: any) {
      toast({ message: err?.message || 'Reset Error', alertColor: 'error' })
    }
  }

  const forgotPasswordSubmit = async (code: string, newPassword: string) => {
    if (!state?.email) return
    try {
      await Auth.forgotPasswordSubmit(state.email, code, newPassword)
      toast({ message: 'Password was changed', alertColor: 'success' })

      dispatch({
        type: 'email',
        payload: { email: '' },
      })
    } catch (err: any) {
      toast({ message: err?.message || 'Reset Error', alertColor: 'error' })
    }
  }

  const completeNewPassword = async (newPassword: string) => {
    try {
      await Auth.completeNewPassword(state.user, newPassword)
      toast({ message: 'Password was changed', alertColor: 'success' })

      const updated = await Auth.currentAuthenticatedUser()

      if (updated) {
        dispatch({
          type: 'login',
          payload: {
            user: updated,
          },
        })
      }

      dispatch({ type: 'new_password_required' })
    } catch (err: any) {
      toast({
        message: err?.message || 'Error Completing New Password',
        alertColor: 'error',
      })
    }
  }

  const confirmEmail = async (code: string) => {
    if (!state?.email) return
    try {
      await Auth.confirmSignUp(state.email, code, {
        forceAliasCreation: false,
      })
      toast({ message: 'Registration is completed', alertColor: 'success' })

      dispatch({
        type: 'email',
        payload: { email: '' },
      })

      Hub.listen('auth', ({ payload }) => {
        const { event } = payload
        if (event === 'autoSignIn') {
          const user = payload.data
          dispatch({
            type: 'login',
            payload: {
              user,
            },
          })
        }
      })
    } catch (err: any) {
      toast({
        message: err?.message || 'Confirmation Error',
        alertColor: 'error',
      })
    }
  }

  const resendCode = async () => {
    if (!state?.email) return
    try {
      await Auth.resendSignUp(state.email)
      toast({
        message: 'Code was sent. Check your email.',
        alertColor: 'success',
      })
    } catch (err: any) {
      toast({
        message: err?.message || 'Sending Code Error',
        alertColor: 'error',
      })
    }
  }

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.changePassword(user, oldPassword, newPassword)
      toast({ message: 'Password was changed', alertColor: 'success' })

      const updated = await Auth.currentAuthenticatedUser()
      if (updated) {
        dispatch({
          type: 'login',
          payload: {
            user: updated,
          },
        })
      }
    } catch (err: any) {
      toast({
        message: err?.message || 'Changing Password Error',
        alertColor: 'error',
      })
    }
  }

  const updateProfile = async (attributes: UserAttributes) => {
    try {
      const user = await Auth.currentAuthenticatedUser()
      await Auth.updateUserAttributes(user, attributes)
      toast({ message: 'Personal info was updated', alertColor: 'success' })

      const updated = await Auth.currentAuthenticatedUser()
      if (updated) {
        dispatch({
          type: 'login',
          payload: {
            user: updated,
          },
        })
      }
    } catch (err: any) {
      toast({ message: err?.message || 'Updating Error', alertColor: 'error' })
    }
  }

  const sendVerifCode = async () => {
    try {
      await Auth.verifyCurrentUserAttribute('email')
      toast({
        message: 'Code was sent. Check your email.',
        alertColor: 'success',
      })
    } catch (err: any) {
      toast({
        message: err?.message || 'Sending Code Error',
        alertColor: 'error',
      })
    }
  }

  const verifyEmail = async (code: string) => {
    try {
      await Auth.verifyCurrentUserAttributeSubmit('email', code)
      toast({ message: 'Email was verified', alertColor: 'success' })
      dispatch({ type: 'email_verified' })
    } catch (err: any) {
      toast({
        message: err?.message || 'Email Verification Error',
        alertColor: 'error',
      })
    }
  }

  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />
  }

  return (
    <AWSCognitoContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        resetPassword,
        forgotPasswordSubmit,
        confirmEmail,
        resendCode,
        updateProfile,
        changePassword,
        sendVerifCode,
        verifyEmail,
        refreshToken,
        completeNewPassword,
      }}>
      {children}
    </AWSCognitoContext.Provider>
  )
}

export default AWSCognitoContext
